import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { decodeHtmlEntities } from '@hub/utils/string-utils';
import { Stack, Typography, useTheme } from '@mui/material';
import { formatDate, formatExpirationDate } from '@monorepo/client-common';
import { Account } from '@monorepo/types';

interface HeaderDetailsProps {
  compact?: boolean;
  account: Account;
}

export const HeaderDetails: FC<HeaderDetailsProps> = ({ compact, account }) => {
  const { t } = useTranslation('dashboardHeader');
  const theme = useTheme();

  const valueProps = {
    fontSize: {
      xs: theme.typography.pxToRem(14),
      sm: theme.typography.pxToRem(16),
    },
  };

  const getAccountDatesText = () => {
    if (account.expireDate) {
      return decodeHtmlEntities(
        t('details.dates', {
          startDate: formatDate(account.createdAt),
          endDate: formatExpirationDate(account.expireDate),
        }),
      );
    }

    return decodeHtmlEntities(
      t('details.startDate', {
        startDate: formatDate(account.createdAt),
      }),
    );
  };

  return (
    <Stack direction="row" flex={1} gap={3} alignItems="center">
      <Typography
        variant="body1"
        sx={(theme) => ({
          color: theme.palette.text.third,
          fontSize: theme.typography.pxToRem(compact ? 12 : 14),
        })}
        {...valueProps}
      >
        {t('details.orderId', { id: account.purchaseId })}
      </Typography>
      {!compact && (
        <>
          <Typography
            variant="body1"
            sx={(theme) => ({
              color: theme.palette.text.third,
            })}
            {...valueProps}
          >
            {t('details.platform', {
              platform: account.tradingSystem.toUpperCase(),
            })}
          </Typography>
          <Typography
            variant="body1"
            sx={(theme) => ({
              color: theme.palette.text.third,
            })}
            {...valueProps}
          >
            {getAccountDatesText()}
          </Typography>
        </>
      )}
    </Stack>
  );
};
